import { useMemo, useState } from 'react';
import * as dayjs from 'dayjs';
import {
    Alert,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Snackbar,
    TextField,
    useTheme,
} from '@mui/material';
import PageHeader from './pageHeader';
import ReceiptsTable from './receiptsTable';
import ReceiptDropzone from './receiptDropzone';
import { buildReimbursementPacket, downloadPdfBytes, fillReimbursementForm } from './generateReimbursementPdf';
import { digitsFromCurrencyInput, dollarsFromDigits, formatCurrencyFromDigits, formatPhoneInput } from './formatHelpers';

const REQUEST_TYPES = ['Reimbursement', 'Check Request', 'Donation'];
const APPROVED_BY_TYPES = ['Board member', 'Youth Leader', 'Other'];

const emptyReceiptRow = () => ({ purchasedFrom: '', amountDigits: '', purpose: '' });

// Formats an ISO ("YYYY-MM-DD", the native <input type="date"> value) date
// string as MM/DD/YYYY for the PDF - or '' if nothing was entered.
const formatIsoDateForPdf = (isoDate) => (isoDate ? dayjs(isoDate).format('MM/DD/YYYY') : '');

// Turns a short free-text summary into a filename-safe slug, e.g.
// "Web Hosting" -> "Web-Hosting", "AV gear (Q3)" -> "AV-gear-Q3".
const slugifyForFilename = (text) =>
    text.trim().replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const ReimbursementForm = () => {
    const theme = useTheme();

    const [summary, setSummary] = useState('');
    const [date, setDate] = useState(() => dayjs().format('YYYY-MM-DD'));
    const [requestType, setRequestType] = useState('Reimbursement');
    const [payableTo, setPayableTo] = useState('');
    const [checkAmountDigits, setCheckAmountDigits] = useState('');
    const [phone, setPhone] = useState('');
    const [purpose, setPurpose] = useState('');
    const [needBy, setNeedBy] = useState('');
    const [requestedBy, setRequestedBy] = useState('');
    const [approvedBy, setApprovedBy] = useState('');
    const [approvedByType, setApprovedByType] = useState('');
    const [receipts, setReceipts] = useState([emptyReceiptRow()]);
    const [attachments, setAttachments] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const grandTotal = useMemo(
        () => receipts.reduce((sum, receipt) => sum + dollarsFromDigits(receipt.amountDigits), 0),
        [receipts]
    );

    const handleReceiptChange = (index, field, value) => {
        setReceipts((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const addReceiptRow = () => setReceipts((rows) => [...rows, emptyReceiptRow()]);

    const removeReceiptRow = (index) => setReceipts((rows) => rows.filter((_, i) => i !== index));

    const handleFilesAdded = (newFiles) => setAttachments((files) => [...files, ...newFiles]);

    const removeAttachment = (index) => setAttachments((files) => files.filter((_, i) => i !== index));

    const handleGenerate = async () => {
        setError('');
        setSuccess('');

        if (!payableTo.trim() || !requestedBy.trim()) {
            setError('Please fill in at least "Make Check Payable to" and "Requested By" before generating the form.');
            return;
        }

        setGenerating(true);
        try {
            const formValues = {
                date: formatIsoDateForPdf(date),
                requestType,
                payableTo,
                checkAmount: checkAmountDigits ? dollarsFromDigits(checkAmountDigits) : '',
                phone,
                purpose,
                needBy: formatIsoDateForPdf(needBy),
                requestedBy,
                approvedBy,
                approvedByType,
                receipts: receipts.map((receipt) => ({
                    purchasedFrom: receipt.purchasedFrom,
                    purpose: receipt.purpose,
                    amount: receipt.amountDigits ? dollarsFromDigits(receipt.amountDigits) : '',
                })),
                grandTotal,
            };

            const filledFormBytes = await fillReimbursementForm(formValues);
            const packetBytes = await buildReimbursementPacket(filledFormBytes, attachments);

            const fileDateStamp = date || dayjs().format('YYYY-MM-DD');
            const summarySlug = slugifyForFilename(summary);
            const filename = summarySlug
                ? `Bethel-Reimbursement-${summarySlug}-${fileDateStamp}.pdf`
                : `Bethel-Reimbursement-${fileDateStamp}.pdf`;
            downloadPdfBytes(packetBytes, filename);
            setSuccess('PDF downloaded.');
        } catch (err) {
            setError('Something went wrong generating the PDF. Please try again.');
        }
        setGenerating(false);
    };

    const mainStyle = {
        fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.palette.brand.linen,
        paddingTop: 'var(--nav-height)',
        minHeight: '100vh',
        boxSizing: 'border-box',
    };

    const sectionStyle = {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.brand.border}`,
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
    };

    const sectionHeadingStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: '13px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.brand.brass,
        margin: '0 0 14px',
    };

    const fieldRowStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '16px',
    };

    const fieldStyle = { flex: '1 1 220px' };

    return (
        <div style={mainStyle}>
            <div style={{ maxWidth: '900px', margin: '0 auto', padding: '36px 24px 56px' }}>
                <PageHeader eyebrow="Finance" heading="Donations & Reimbursements" />

                <p
                    style={{
                        fontFamily: theme.typography.paragraphFontFamily,
                        fontSize: '13px',
                        color: theme.palette.text.secondary,
                        margin: '0 0 24px',
                        maxWidth: '620px',
                    }}
                >
                    Fill out the form below to generate a filled copy of the official Reimbursement/Check Request
                    Authorization Form. Attach your receipts (PDF, PNG, or JPEG) and we'll generate a single PDF
                    with the filled form followed by every receipt, ready to submit.
                </p>

                <div style={sectionStyle}>
                    <div style={sectionHeadingStyle}>Request Details</div>

                    <div style={fieldRowStyle}>
                        <div style={{ flex: '1 1 100%' }}>
                            <TextField
                                label="Summary"
                                fullWidth
                                placeholder="e.g. Web hosting"
                                helperText="A few words describing what this is for - used to name the downloaded file."
                                inputProps={{ maxLength: 40 }}
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <TextField
                                label="Date"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '13px' }}>Request Type</FormLabel>
                                <RadioGroup
                                    row
                                    value={requestType}
                                    onChange={(e) => setRequestType(e.target.value)}
                                >
                                    {REQUEST_TYPES.map((type) => (
                                        <FormControlLabel key={type} value={type} control={<Radio size="small" />} label={type} />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </div>
                    </div>

                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <TextField
                                label="Make Check Payable to"
                                fullWidth
                                value={payableTo}
                                onChange={(e) => setPayableTo(e.target.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <TextField
                                label="Check Amount"
                                fullWidth
                                placeholder="$0.00"
                                inputMode="numeric"
                                helperText={requestType === 'Donation' ? 'No check needed? Enter $0.00.' : ' '}
                                value={formatCurrencyFromDigits(checkAmountDigits)}
                                onChange={(e) => setCheckAmountDigits(digitsFromCurrencyInput(e.target.value))}
                            />
                        </div>
                    </div>

                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <TextField
                                label="Phone #"
                                fullWidth
                                placeholder="(000) 000-0000"
                                inputMode="tel"
                                value={phone}
                                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                            />
                        </div>
                    </div>

                    <div style={fieldRowStyle}>
                        <div style={{ flex: '1 1 100%' }}>
                            <TextField
                                label="Purpose/Reason for Check Request"
                                fullWidth
                                multiline
                                rows={2}
                                value={purpose}
                                onChange={(e) => setPurpose(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <TextField
                                label="Need By"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={needBy}
                                onChange={(e) => setNeedBy(e.target.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <TextField
                                label="Requested By"
                                fullWidth
                                value={requestedBy}
                                onChange={(e) => setRequestedBy(e.target.value)}
                            />
                        </div>
                    </div>

                    <div style={fieldRowStyle}>
                        <div style={fieldStyle}>
                            <TextField
                                label="Approved By"
                                fullWidth
                                value={approvedBy}
                                onChange={(e) => setApprovedBy(e.target.value)}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <FormControl>
                                <FormLabel sx={{ fontSize: '13px' }}>Approver Role</FormLabel>
                                <RadioGroup
                                    row
                                    value={approvedByType}
                                    onChange={(e) => setApprovedByType(e.target.value)}
                                >
                                    {APPROVED_BY_TYPES.map((type) => (
                                        <FormControlLabel key={type} value={type} control={<Radio size="small" />} label={type} />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </div>
                    </div>
                </div>

                <div style={sectionStyle}>
                    <div style={sectionHeadingStyle}>Receipts</div>
                    <ReceiptsTable
                        receipts={receipts}
                        onRowChange={handleReceiptChange}
                        onAddRow={addReceiptRow}
                        onRemoveRow={removeReceiptRow}
                        grandTotal={grandTotal}
                    />
                </div>

                <div style={sectionStyle}>
                    <div style={sectionHeadingStyle}>Attach Receipt Files (optional)</div>
                    <ReceiptDropzone files={attachments} onFilesAdded={handleFilesAdded} onRemoveFile={removeAttachment} />
                </div>

                <Button
                    variant="contained"
                    size="large"
                    onClick={handleGenerate}
                    disabled={generating}
                    fullWidth
                >
                    {generating ? 'Generating…' : 'Generate PDF'}
                </Button>
            </div>

            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            </Snackbar>
            <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')}>
                <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
            </Snackbar>
        </div>
    );
};

export default ReimbursementForm;
